import { useState } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { toast } from 'sonner'
import { Loader2, AlertCircle, Link2 } from 'lucide-react'
import { SupervisaoVendasService } from '../../services/supervisaoVendas.service'
import type { VinculoResolvido } from '../../services/vinculoResolver'

interface ContratosSemVinculoModalProps {
  isOpen: boolean
  onClose: () => void
  vinculos: VinculoResolvido[]
  relacionadores: { relacionadorId: string; nome: string }[]
  onSuccess: () => void
}

export function ContratosSemVinculoModal({
  isOpen,
  onClose,
  vinculos,
  relacionadores,
  onSuccess,
}: ContratosSemVinculoModalProps) {
  const semVinculo = vinculos.filter(v => v.origem === 'SEM_VINCULO')
  const [selectedRelacionadores, setSelectedRelacionadores] = useState<Record<string, string>>({})
  const [loadingContratos, setLoadingContratos] = useState<Record<string, boolean>>({})

  const handleVincular = async (contratoId: string) => {
    const relacionadorId = selectedRelacionadores[contratoId]
    if (!relacionadorId) {
      toast.error('Por favor, selecione um relacionador.')
      return
    }

    try {
      setLoadingContratos(prev => ({ ...prev, [contratoId]: true }))
      await SupervisaoVendasService.vincularContrato(contratoId, relacionadorId)
      toast.success('Contrato vinculado com sucesso!')
      onSuccess()
    } catch (error) {
      console.error('Erro ao vincular contrato:', error)
      toast.error('Erro ao salvar o vínculo do contrato.')
    } finally {
      setLoadingContratos(prev => ({ ...prev, [contratoId]: false }))
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl font-bold text-yellow-900">
            <AlertCircle className="h-6 w-6 text-yellow-600" />
            Contratos Aguardando Vínculo
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground mb-4">
          Estes contratos foram identificados no período selecionado, mas não possuem um relacionador associado. Selecione um relacionador de sua equipe para vincular cada um manualmente.
        </p>

        {semVinculo.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center border rounded-lg bg-green-50/20 border-green-100">
            <div className="rounded-full bg-green-100 p-3 mb-3 text-green-700">
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="font-semibold text-green-900">Tudo em dia!</h3>
            <p className="text-sm text-green-700 mt-1">Nenhum contrato sem vínculo no período selecionado.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {semVinculo.map((v) => {
              const isLoading = loadingContratos[v.contratoId]
              const valorFormatado = v.valorBem
                ? `R$ ${Number(v.valorBem).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                : '—'
              const dataFormatada = v.createdAt
                ? new Date(v.createdAt).toLocaleDateString('pt-BR')
                : '—'

              return (
                <div
                  key={v.contratoId}
                  className="flex flex-col md:flex-row md:items-center justify-between p-4 border rounded-lg gap-4 bg-card shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-sm">Contrato #{v.contratoId}</span>
                      <span className="text-xs text-muted-foreground">({v.descricao})</span>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span><strong>Data:</strong> {dataFormatada}</span>
                      <span><strong>Valor:</strong> {valorFormatado}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 min-w-[280px]">
                    <Select
                      value={selectedRelacionadores[v.contratoId] || ''}
                      onValueChange={(val) =>
                        setSelectedRelacionadores(prev => ({ ...prev, [v.contratoId]: val }))
                      }
                      disabled={isLoading}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Selecione o relacionador..." />
                      </SelectTrigger>
                      <SelectContent>
                        {relacionadores.map((r) => (
                          <SelectItem key={r.relacionadorId} value={r.relacionadorId}>
                            {r.nome}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <Button
                      onClick={() => handleVincular(v.contratoId)}
                      disabled={isLoading || !selectedRelacionadores[v.contratoId]}
                      size="sm"
                      className="bg-yellow-600 hover:bg-yellow-700 text-white flex items-center gap-1 shrink-0"
                    >
                      {isLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Link2 className="h-4 w-4" />
                      )}
                      Vincular
                    </Button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
